import React, {memo, useEffect, useState} from 'react';
import fileDownload from 'react-file-download';
import {
    FormGroup,
    Input
} from 'reactstrap';
import {
    AppleCard,
    AppleCardHeader,
    AppleCardBody,
    AppleButton,
    AppleTable,
    AppleInput,
    AppleLabel
} from '../../../components/AppleStyles';
import { FaUpload } from 'react-icons/fa';

export const GuestHazardAttachmentSection = memo(({
    attachments = [],
    onAttachmentsChange
}) => {
    // Ensure each attachment has a stable _key property
    const ensureKeys = (arr) =>
        arr.map((att, idx) =>
            att && att._key
                ? att
                : { ...att, _key: att._key || `attachment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` }
        );

    const [internalAttachments, setInternalAttachments] = useState(ensureKeys(attachments));

    // Sync internal state with prop changes, but only if the array reference changes
    useEffect(() => {
        setInternalAttachments(ensureKeys(attachments));
    }, [attachments]);

    const generateKey = () => `attachment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const addNewRow = () => {
        const newAttachment = {
            filename: '',
            name: '',
            description: '',
            createdBy: null,
            updatedAt: null,
            updatedBy: null,
            file: null,
            path: null,
            _key: generateKey(),
        };
        const updatedAttachments = [...internalAttachments, newAttachment];
        setInternalAttachments(updatedAttachments);
        onAttachmentsChange(updatedAttachments);
    };

    const updateAttachment = (keyToUpdate, field, value) => {
        const updated = internalAttachments.map(att =>
            att._key === keyToUpdate ? { ...att, [field]: value } : att
        );
        setInternalAttachments(updated);
        onAttachmentsChange(updated);
    };

    const handleFileChange = (keyToUpdate, file) => {
        if (file && file.size > 5 * 1024 * 1024) {
            alert("File size should not exceed 5MB");
            return;
        }

        const updated = internalAttachments.map(att =>
            att._key === keyToUpdate
                ? { ...att, filename: file ? file.name : '', name: file ? file.name : '', file: file, path: null }
                : att
        );
        setInternalAttachments(updated);
        onAttachmentsChange(updated);
    };

    const deleteAttachment = (keyToDelete) => {
        const updated = internalAttachments.filter(att => att._key !== keyToDelete);
        setInternalAttachments(updated);
        onAttachmentsChange(updated);
    };

    const viewAttachment = (attachment) => {
        if (attachment.file instanceof File) {
            const url = URL.createObjectURL(attachment.file);
            window.open(url, "_blank");
        } else if (attachment.fullPath || attachment.path) {
            fetch(attachment.fullPath || attachment.path)
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }
                    return response.blob();
                })
                .then((blob) => {
                    fileDownload(
                        blob,
                        attachment.fileName || attachment.filename || "download",
                    );
                })
                .catch((error) => {
                    console.error("Error downloading file:", error);
                    alert("Error downloading file. Please try again.");
                });
        }
    };

    return (
        <AppleCard>
            <AppleCardHeader>
                <h2>Supporting Evidence</h2>
                <AppleButton color="primary" onClick={addNewRow}>Add File</AppleButton>
            </AppleCardHeader>
            <AppleCardBody>
                <AppleTable hover responsive>
                    <thead>
                        <tr>
                            <th>File Name</th>
                            <th>Description</th>
                            <th>Upload File</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {internalAttachments.map((attachment) => (
                            <tr key={attachment._key}>
                                <td>
                                    {attachment.filename || attachment.name}
                                </td>
                                <td>
                                    <AppleInput
                                        type="text"
                                        value={attachment.description}
                                        onChange={(e) => updateAttachment(attachment._key, 'description', e.target.value)}
                                        placeholder="What does this file show?"
                                    />
                                </td>
                                <td>
                                    <FormGroup style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                            type="file"
                                            id={`file-${attachment._key}`}
                                            onChange={(e) => handleFileChange(attachment._key, e.target.files[0])}
                                            style={{ display: 'none' }}
                                            onClick={(event) => { event.target.value = null }}
                                        />
                                        <AppleButton
                                            color="primary"
                                            type="button"
                                            onClick={() => document.getElementById(`file-${attachment._key}`).click()}
                                            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                                        >
                                            <FaUpload style={{ marginRight: 4 }} />
                                            {attachment.file || attachment.path ? 'Change File' : 'Upload File'}
                                        </AppleButton>
                                        {attachment.filename || attachment.name ? (
                                            <span style={{ fontSize: '0.95em', color: '#555', marginLeft: 6, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle' }} title={attachment.filename || attachment.name}>
                                                {attachment.filename || attachment.name}
                                            </span>
                                        ) : null}
                                    </FormGroup>
                                </td>
                                <td>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <AppleButton
                                            color="secondary"
                                            size="sm"
                                            onClick={() => viewAttachment(attachment)}
                                            disabled={!attachment.file && !attachment.path && !attachment.fullPath}
                                        >
                                            {attachment.fullPath || attachment.path ? 'Download' : 'View'}
                                        </AppleButton>
                                        <AppleButton
                                            color="danger"
                                            size="sm"
                                            onClick={() => deleteAttachment(attachment._key)}
                                        >
                                            Delete
                                        </AppleButton>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {internalAttachments.length === 0 && (
                            <tr>
                                <td colSpan="4" className="text-center text-muted py-4">
                                    No files attached. Add photos, videos or documents that help describe the hazard.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </AppleTable>
            </AppleCardBody>
        </AppleCard>
    );
}); 